import { create } from 'zustand';
import { supabase } from '../supabase';
import { friendlyDbError } from '../utils/friendlyDbError';
import type { Unavailability } from '../calendar/team/unavailability';

/**
 * Indisponibilità del calendario (`calendar_unavailability`): periodi con date in cui una persona non è operativa.
 * Le righe di tutta l'azienda nei periodi caricati; chi può scrivere lo decide la RLS (proprio calendario,
 * o permesso "Segna le indisponibilità dei colleghi").
 */
interface CalendarUnavailabilityState {
  rows: Record<string, Unavailability>;
  error: string | null;
  /** Carica le indisponibilità che toccano il periodo [from, to). */
  fetchRange: (organizationId: string, from: Date, to: Date) => Promise<void>;
  save: (row: Omit<Unavailability, 'id' | 'created_by'> & { id?: string | null }) => Promise<{ ok: true } | { ok: false; error: string }>;
  remove: (id: string) => Promise<{ ok: true } | { ok: false; error: string }>;
}

const COLUMNS = 'id, organization_id, user_id, title, note, start_time, end_time, all_day, created_by';

export const useCalendarUnavailabilityStore = create<CalendarUnavailabilityState>((set, get) => ({
  rows: {},
  error: null,

  fetchRange: async (organizationId, from, to) => {
    const { data, error } = await supabase
      .from('calendar_unavailability')
      .select(COLUMNS)
      .eq('organization_id', organizationId)
      .lt('start_time', to.toISOString())
      .gt('end_time', from.toISOString());
    // Tabella non ancora presente (migration non applicata): il calendario lavora come prima
    if (error) { console.warn('[calendarUnavailability] non disponibile:', error.message); set({ error: friendlyDbError(error) }); return; }
    const fresh = (data || []) as Unavailability[];
    const fromMs = from.getTime();
    const toMs = to.getTime();
    // Nel periodo ricaricato vale quello che c'e' nel database (righe cancellate da altri spariscono)
    const kept = Object.values(get().rows).filter(r => !(new Date(r.start_time).getTime() < toMs && new Date(r.end_time).getTime() > fromMs));
    set({ rows: Object.fromEntries([...kept, ...fresh].map(r => [r.id, r])), error: null });
  },

  save: async (row) => {
    const { id, ...values } = row;
    const res = id
      ? await supabase.from('calendar_unavailability').update(values).eq('id', id).select(COLUMNS).single()
      : await supabase.from('calendar_unavailability').insert(values).select(COLUMNS).single();
    if (res.error || !res.data) return { ok: false, error: res.error ? friendlyDbError(res.error) : 'Indisponibilità non salvata' };
    const saved = res.data as Unavailability;
    set({ rows: { ...get().rows, [saved.id]: saved } });
    return { ok: true };
  },

  remove: async (id) => {
    // Senza permesso la RLS non cancella nulla e non da' errore: si controlla che la riga sia sparita davvero
    const { data, error } = await supabase.from('calendar_unavailability').delete().eq('id', id).select('id');
    if (error) return { ok: false, error: friendlyDbError(error) };
    if (!data?.length) return { ok: false, error: 'Non puoi cancellare questa indisponibilità' };
    const { [id]: _gone, ...rest } = get().rows;
    set({ rows: rest });
    return { ok: true };
  },
}));
